import useSWR from 'swr';
import { backendClient } from '@/lib/api-client';
import type { MatterMember } from '@/types/base';

const fetcher = async (url: string) => {
  const response = await backendClient.get(url);
  return response.data;
};

export type FirmSortOrder = 'asc' | 'desc';

export type FirmMatterSortField = 'name' | 'region' | 'documents_count' | 'messages_count';

export interface FirmMatterItem {
  matter_id: number;
  matter_guid: string;
  matter_name: string;
  matter_no: string | null;
  case_reference: string | null;
  region: string | null;
  forum: string | null;
  is_active: boolean;
  documents_count: number;
  messages_count: number;
  users_count: number;
  members: MatterMember[];
}

export interface FirmMattersResponse {
  matters: FirmMatterItem[];
  total_count: number;
  page: number;
  page_size: number;
}

export const FIRM_MATTERS_PAGE_SIZE = 50;

export const useFirmMatters = (options: {
  q?: string;
  memberGuids?: string[];
  sortField?: FirmMatterSortField;
  sortOrder?: FirmSortOrder;
  page?: number;
  pageSize?: number;
}) => {
  const q = options.q?.trim() ?? '';
  const memberGuids = options.memberGuids ?? [];
  const sortField = options.sortField ?? 'documents_count';
  const sortOrder = options.sortOrder ?? 'desc';
  const page = options.page ?? 1;
  const pageSize = options.pageSize ?? FIRM_MATTERS_PAGE_SIZE;

  const params = new URLSearchParams();
  if (q) params.set('q', q);
  for (const guid of memberGuids) {
    params.append('member_guid', guid);
  }
  params.set('sort_field', sortField);
  params.set('sort_order', sortOrder);
  params.set('page', String(page));
  params.set('page_size', String(pageSize));

  const key = `/firm/matters?${params.toString()}`;
  const { data, isLoading, error, mutate } = useSWR<FirmMattersResponse>(key, fetcher, {
    revalidateOnFocus: false,
    keepPreviousData: true,
  });

  return {
    data,
    isLoading,
    error,
    refresh: mutate,
  };
};

export type FirmSyncSortField = 'provider' | 'owner' | 'last_synced';

export interface FirmSyncItem {
  id: number;
  matter_guid: string;
  matter_name: string;
  matter_no: string | null;
  case_reference: string | null;
  provider_type: string;
  provider_label: string;
  source_label: string | null;
  destination_label: string | null;
  is_active: boolean;
  status: string;
  created_by_user_name: string | null;
  created_by_user_email: string | null;
  last_synced_at: string | null;
  created_at: string | null;
}

export interface FirmSyncsResponse {
  syncs: FirmSyncItem[];
  total_count: number;
  page: number;
  page_size: number;
}

export const FIRM_SYNCS_PAGE_SIZE = 50;

/** Fixed provider options for the Syncs tab filter (not derived from the current page). */
export const FIRM_SYNC_PROVIDER_OPTIONS = ['iManage', 'OneDrive', 'SharePoint', 'Outlook'] as const;

/** Fixed status options for the Syncs tab filter (not derived from the current page). */
export const FIRM_SYNC_STATUS_OPTIONS = [
  'Active',
  'Inactive',
  'Sync Blocked - OneDrive Reconnection Required',
  'Sync Blocked - SharePoint Reconnection Required',
  'OneDrive folder no longer exists',
  'SharePoint folder no longer exists',
  'Sync Blocked - iManage Reconnection Required',
  'Sync Blocked - Invalid Source',
  'Sync Paused - Repeated Failures',
  'Sync Paused',
  'Sync Paused - Account Deactivated',
] as const;

export const useFirmSyncs = (options: {
  q?: string;
  providers?: string[];
  statuses?: string[];
  sortField?: FirmSyncSortField;
  sortOrder?: FirmSortOrder;
  page?: number;
  pageSize?: number;
}) => {
  const q = options.q?.trim() ?? '';
  const providers = options.providers ?? [];
  const statuses = options.statuses ?? [];
  const sortField = options.sortField ?? 'last_synced';
  const sortOrder = options.sortOrder ?? 'desc';
  const page = options.page ?? 1;
  const pageSize = options.pageSize ?? FIRM_SYNCS_PAGE_SIZE;

  const params = new URLSearchParams();
  if (q) params.set('q', q);
  for (const provider of providers) {
    params.append('provider', provider);
  }
  for (const status of statuses) {
    params.append('status', status);
  }
  params.set('sort_field', sortField);
  params.set('sort_order', sortOrder);
  params.set('page', String(page));
  params.set('page_size', String(pageSize));

  const key = `/firm/syncs?${params.toString()}`;
  const { data, isLoading, error, mutate } = useSWR<FirmSyncsResponse>(key, fetcher, {
    revalidateOnFocus: false,
    keepPreviousData: true,
  });

  return {
    data,
    isLoading,
    error,
    refresh: mutate,
  };
};

export type FirmAuditAction = 'insert' | 'view' | 'download' | 'delete';

export type FirmAuditChannel = 'ui' | 'case_partner' | 'superadmin';

export interface FirmAuditItem {
  id: number;
  created_at: string;
  action: FirmAuditAction;
  channel: FirmAuditChannel;
  display_name: string | null;
  folder_path: string | null;
  actor_name: string | null;
  actor_email: string | null;
  matter_guid: string | null;
  matter_name: string | null;
  matter_no: string | null;
  case_reference: string | null;
}

export interface FirmAuditsResponse {
  audits: FirmAuditItem[];
  total_count: number;
  page: number;
  page_size: number;
}

export const FIRM_AUDIT_PAGE_SIZE = 50;

export type FirmAuditQueryOptions = {
  matterGuid?: string;
  dateFrom?: string;
  dateTo?: string;
  actions?: FirmAuditAction[];
  channels?: FirmAuditChannel[];
  file?: string;
  person?: string;
  sortOrder?: FirmSortOrder;
  page?: number;
  pageSize?: number;
};

function buildFirmAuditParams(
  options: FirmAuditQueryOptions,
  { includePagination }: { includePagination: boolean },
): URLSearchParams {
  const matterGuid = options.matterGuid?.trim() ?? '';
  const dateFrom = options.dateFrom?.trim() ?? '';
  const dateTo = options.dateTo?.trim() ?? '';
  const actions = options.actions ?? [];
  const channels = options.channels ?? [];
  const file = options.file?.trim() ?? '';
  const person = options.person?.trim() ?? '';
  const sortOrder = options.sortOrder ?? 'desc';
  const page = options.page ?? 1;
  const pageSize = options.pageSize ?? FIRM_AUDIT_PAGE_SIZE;

  const params = new URLSearchParams();
  if (matterGuid) params.set('matter_guid', matterGuid);
  if (dateFrom) params.set('date_from', dateFrom);
  if (dateTo) params.set('date_to', dateTo);
  for (const action of actions) {
    params.append('action', action);
  }
  for (const channel of channels) {
    params.append('channel', channel);
  }
  if (file) params.set('file', file);
  if (person) params.set('person', person);
  params.set('sort_order', sortOrder);
  if (includePagination) {
    params.set('page', String(page));
    params.set('page_size', String(pageSize));
  }
  return params;
}

export const useFirmAudit = (options: FirmAuditQueryOptions = {}) => {
  const params = buildFirmAuditParams(options, { includePagination: true });
  const key = `/firm/audit?${params.toString()}`;
  const { data, isLoading, error, mutate } = useSWR<FirmAuditsResponse>(key, fetcher, {
    revalidateOnFocus: false,
    keepPreviousData: true,
  });

  return {
    data,
    isLoading,
    error,
    refresh: mutate,
  };
};

/** Download the firm audit CSV for the current filters. Throws on failure. */
export async function downloadFirmAuditCsv(
  options: Omit<FirmAuditQueryOptions, 'page' | 'pageSize'> = {},
): Promise<void> {
  const params = buildFirmAuditParams(options, { includePagination: false });
  const response = await backendClient.get(`/firm/audit/export.csv?${params.toString()}`, {
    responseType: 'blob',
  });

  const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8' });
  const url = window.URL.createObjectURL(blob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = 'file-audit.csv';
  document.body.appendChild(anchor);
  anchor.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(anchor);
}
